
import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Share2, Copy, Link } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter
} from '@/components/ui/dialog';
import { useInviteLink } from '@/hooks/travel/useInviteLink';
import { Input } from '@/components/ui/input';

interface InviteLinkShareProps {
  tripId: string;
  tripName?: string;
}

export const InviteLinkShare: React.FC<InviteLinkShareProps> = ({ 
  tripId,
  tripName = 'this trip'
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [inviteLink, setInviteLink] = useState<string | null>(null);
  const { generateShareLink, copyLinkToClipboard, isSharing } = useInviteLink(tripId);

  const handleOpenDialog = async () => {
    const link = await generateShareLink();
    if (link) {
      setInviteLink(link);
      setIsOpen(true);
    }
  };

  const handleCopyLink = async () => {
    if (inviteLink) {
      await navigator.clipboard.writeText(inviteLink);
      toast({
        title: "Link Copied!",
        description: "Invitation link copied to clipboard"
      });
      setIsOpen(false);
    } else {
      await copyLinkToClipboard();
    }
  };

  return (
    <>
      <Button
        variant="outline"
        size="sm"
        onClick={handleOpenDialog}
        className="flex items-center gap-1"
        disabled={isSharing}
      >
        <Share2 className="h-4 w-4" />
        Invite
      </Button>

      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Share Trip Invitation</DialogTitle>
            <DialogDescription>
              Anyone with this link can join {tripName}.
            </DialogDescription>
          </DialogHeader>
          
          <div className="flex items-center space-x-2 mt-4">
            <div className="grid flex-1 gap-2">
              <Input
                value={inviteLink || ''}
                readOnly
                className="w-full"
              />
            </div>
            <Button size="sm" onClick={handleCopyLink}>
              <Copy className="h-4 w-4" />
            </Button>
          </div>
          
          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setIsOpen(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

import { toast } from '@/hooks/use-toast';
